import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { forwardRef, useEffect } from 'react';

import { useAudioTrigger } from '../../../controllers/audioController/hooks';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { Icon } from '../../icons/icon';
import { Icons } from '../../icons/iconNames';

import styles from './IconButton.module.css';

interface IconButtonCoreProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'onClick'> {
  active?: boolean;
  autoCall?: boolean;
  ghost?: boolean;
  large?: boolean;
  light?: boolean;
  onClick?: (event?: MouseEvent<HTMLButtonElement>) => void;
  showBadge?: boolean;
  small?: boolean;
  squared?: boolean;
  timer?: number;
  timerIncreasing?: boolean;
  timerReversed?: boolean;
  tiny?: boolean;
}

interface IconButtonIconProps extends IconButtonCoreProps {
  icon: keyof Icons;
  children?: never;
}

interface IconButtonChildrenProps extends IconButtonCoreProps {
  children: ReactNode;
  icon?: never;
}

export type IconButtonProps = IconButtonIconProps | IconButtonChildrenProps;

const IconButton = forwardRef<HTMLSpanElement, IconButtonProps>(function IconButtonFunc(
  {
    active,
    autoCall = true,
    children,
    className = '',
    disabled,
    ghost,
    icon,
    large,
    light,
    onClick,
    showBadge,
    small,
    squared,
    timer,
    timerIncreasing,
    timerReversed,
    tiny,
    ...rest
  },
  forwardedRef
) {
  const mods = useModifiers(
    styles,
    'iconButton',
    {
      active,
      disabled,
      ghost,
      large,
      light,
      small,
      squared,
      timerIncreasing,
      timerReversed,
      tiny,
    },
    { withBaseClass: true }
  );

  const audioTrigger = useAudioTrigger();

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    audioTrigger.play({ name: 'menuClick' });
    onClick?.(event);
  };

  useEffect(() => {
    if (timer && onClick && autoCall) {
      const timerId: number = window.setTimeout(() => {
        onClick();
      }, timer);
      return (): void => {
        window.clearTimeout(timerId);
      };
    }
  }, [timer, onClick, autoCall]);

  return (
    <div className={styles.iconButton__wrapper}>
      <button
        type="button"
        data-testid="icon-button"
        className={`${mods} ${className}`}
        disabled={disabled}
        onClick={handleClick}
        {...rest}
      >
        {/*inner wrapper fixes button scaling on active state issue*/}
        <span className={styles.iconButton__innerWrapper} ref={forwardedRef}>
          <span className={styles.iconButton__content}>{icon ? <Icon icon={icon} /> : children}</span>
          {timer ? (
            <svg className={styles.iconButton__timer}>
              <circle className={styles.iconButton__timerBackground} />
              <circle className={styles.iconButton__timerPath} style={{ animationDuration: `${timer}ms` }} />
            </svg>
          ) : null}
        </span>
        {showBadge ? <span className={styles.iconButton__badge} /> : null}
      </button>
    </div>
  );
});

IconButton.displayName = 'IconButton';

export { IconButton, Icons };
